import React, { useEffect, useState } from 'react';
import { CitationAuditService } from '../admin/citationAudit';
import { CitationAuditResult } from '../types/capabilityCitation';
import { useCapabilityCitationStore } from '../store/capabilityCitations';
import { CheckCircle, AlertTriangle, XCircle, ExternalLink, RefreshCw, Download } from 'lucide-react';

export const CitationAuditDashboard: React.FC = () => {
  const [report, setReport] = useState<CitationAuditResult | null>(null);
  const [details, setDetails] = useState<{ capabilityId: string; issues: string[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const citations = useCapabilityCitationStore(state => state.citations);

  const runAudit = async () => {
    setLoading(true);
    const result = await CitationAuditService.runFullAudit();
    setReport(result.report);
    setDetails(result.details);
    setLoading(false);
  };

  useEffect(() => {
    // Initial quick report (sync)
    setReport(CitationAuditService.getAuditReport());
  }, []);

  const handleExport = () => {
    const csv = CitationAuditService.exportAuditToCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `citation-audit-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!report) return <div>Loading...</div>;

  const healthPercentage = report.totalCapabilities > 0 
    ? Math.round((report.citedCapabilities / report.totalCapabilities) * 100) 
    : 0;

  return (
    <div className="h-full flex flex-col bg-slate-900 text-slate-100 p-6 overflow-hidden">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            Citation Audit Dashboard
            {healthPercentage === 100 ? (
              <CheckCircle className="text-green-500 w-6 h-6" />
            ) : (
              <AlertTriangle className="text-yellow-500 w-6 h-6" />
            )}
          </h2>
          <p className="text-slate-400 text-sm">Legal compliance and source verification status</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={runAudit}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded border border-slate-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Scanning...' : 'Run Full Audit'}
          </button>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-ops-blue hover:bg-blue-600 rounded text-white transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Total Capabilities</div>
          <div className="text-2xl font-bold text-white">{report.totalCapabilities}</div>
        </div>
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Cited & Verified</div>
          <div className="text-2xl font-bold text-green-400">{report.citedCapabilities}</div>
          <div className="text-xs text-slate-500 mt-1">{healthPercentage}% Coverage</div>
        </div>
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Broken Links</div>
          <div className={`text-2xl font-bold ${report.brokenLinks > 0 ? 'text-red-400' : 'text-slate-300'}`}>
            {report.brokenLinks}
          </div>
        </div>
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Outdated (&gt;10y)</div>
          <div className={`text-2xl font-bold ${report.outdatedCitations > 0 ? 'text-yellow-400' : 'text-slate-300'}`}>
            {report.outdatedCitations}
          </div>
        </div>
      </div>

      {/* Issues List */}
      {details.length > 0 && (
        <div className="mb-8 bg-red-900/20 border border-red-900/50 rounded-lg p-4">
          <h3 className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Detected Issues
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {details.map((detail, idx) => (
              <div key={idx} className="text-sm text-red-200 bg-red-900/20 p-2 rounded">
                <span className="font-bold">{detail.capabilityId}:</span> {detail.issues.join(', ')}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full List */}
      <div className="flex-1 bg-slate-800 rounded-lg border border-slate-700 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-700 font-bold text-slate-300">
          Citation Library
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-900 text-slate-400 sticky top-0">
              <tr>
                <th className="p-3 font-medium">Capability</th>
                <th className="p-3 font-medium">Source</th>
                <th className="p-3 font-medium">Title</th>
                <th className="p-3 font-medium">Date</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {citations.map((citation) => (
                <tr key={citation.capabilityId} className="hover:bg-slate-700/50 transition-colors">
                  <td className="p-3">
                    <div className="font-medium text-white">{citation.capabilityName}</div>
                    <div className="text-xs text-slate-500">{citation.capabilityType}</div>
                  </td>
                  <td className="p-3 text-slate-300">{citation.citationSource}</td>
                  <td className="p-3 text-slate-300 max-w-xs truncate" title={citation.citationTitle}>
                    {citation.citationTitle}
                  </td>
                  <td className="p-3 text-slate-400 font-mono text-xs">{citation.datePublished}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs border ${
                      citation.legalStatus === 'public-domain' ? 'bg-green-900/30 border-green-800 text-green-400' :
                      citation.legalStatus === 'unclassified' ? 'bg-blue-900/30 border-blue-800 text-blue-400' :
                      'bg-slate-700 border-slate-600 text-slate-300'
                    }`}>
                      {citation.legalStatus}
                    </span>
                  </td>
                  <td className="p-3">
                    <a 
                      href={citation.citationURL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-cyan-500 hover:text-cyan-400"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
